import React, { useState, useEffect } from 'react';

/**
 * COMPONENTE: AdminPenalizacionesPanel
 * Muestra las MISMAS penalizaciones que ve el técnico en su dashboard
 * Calcula las reglas de penalización basadas en los datos del reporte
 */

const AdminPenalizacionesPanel = ({ reportData, usuarioFFM, apiUrl, token }) => {
    const [loading, setLoading] = useState(false);
    const [metrics, setMetrics] = useState(null);

    // Función para calcular distancia (Haversine)
    const calcularDistancia = (lat1, lon1, lat2, lon2) => {
        const R = 6371000; // Radio de la Tierra en metros
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return Math.round(R * c);
    };

    // Función para obtener el string del día
    const getDayStr = (ts) => {
        if (!ts) return null;
        const d = new Date(ts);
        return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
    };

    // Función para formatear tiempo
    const formatTiempoOperativo = (minutos) => {
        if (minutos === 0) return '0 min';
        const h = Math.floor(minutos / 60);
        const m = minutos % 60;
        if (h === 0) return `${m} min`;
        if (m === 0) return `${h}h 0min`;
        return `${h}h ${m}min`;
    };

    // FUNCIÓN PRINCIPAL: Calcula las penalizaciones igual que TechnicianPage.js
    const calcularMetricasAdherencia = (data) => {
        if (!data) return null;

        const getSentRaw = (p) => p.sentAt || p.submittedAt || p.timestamp;
        
        const segmentos = (data.maintenancePoints || [])
            .filter(p => p.submitted || p.offline)
            .map(p => ({ 
                ts: new Date(getSentRaw(p)).getTime(), 
                loc: p.location,
                id: p.id
            }));

        const noRealizables = (data.unrealizablePoints || [])
            .filter(p => p.submitted || p.offline)
            .map(p => ({ 
                ts: new Date(getSentRaw(p)).getTime(), 
                loc: p.location 
            }));

        const todos = [...segmentos, ...noRealizables]
            .filter(p => !isNaN(p.ts))
            .sort((a, b) => a.ts - b.ts);

        if (todos.length === 0) {
            return {
                tiempoTrabajado: 0,
                tiempoOperativoEfectivo: 0,
                distInicioAFrimero: 0,
                aplicaPenalizacion: false,
                escenario: 'A',
                alertas: [],
                distUltimoACierre: null
            };
        }

        const tiempoTrabajado = data.totalWorkTime || 0;
        const primerPuntoGlobal = todos[0];
        const fechaInicio = data.startTime ? new Date(data.startTime) : null;
        const diaInicio = fechaInicio ? getDayStr(fechaInicio.getTime()) : null;
        const diaPrimerPunto = getDayStr(primerPuntoGlobal.ts);

        const isDifferentDayLogin = diaInicio && diaInicio !== diaPrimerPunto;
        const validTodos = todos.filter(p => getDayStr(p.ts) === diaPrimerPunto);
        const validPrimerPunto = validTodos[0];
        const validUltimoPunto = validTodos[validTodos.length - 1];

        let distInicioAFrimero = 0;
        let aplicaPenalizacion = false;
        let escenario = 'A';

        // Calcular distancia inicio → primer segmento
        if (data.startLocation && validPrimerPunto.loc) {
            distInicioAFrimero = calcularDistancia(
                data.startLocation.latitude,
                data.startLocation.longitude,
                validPrimerPunto.loc.latitude,
                validPrimerPunto.loc.longitude
            );
        }

        // DESDE cuándo contar
        let tsDesde;
        if (isDifferentDayLogin) {
            escenario = 'D';
            aplicaPenalizacion = true;
            tsDesde = validPrimerPunto.ts;
        } else if (fechaInicio && data.startLocation && validPrimerPunto.loc && distInicioAFrimero < 500) {
            tsDesde = fechaInicio.getTime();
        } else {
            tsDesde = validPrimerPunto.ts;
            if (!isDifferentDayLogin && distInicioAFrimero >= 500) {
                aplicaPenalizacion = true;
            }
        }

        // HASTA cuándo contar
        let usarHasta = validUltimoPunto.ts;
        let distUltimoACierre = null;

        const finRecorridoTS = data.endTime ? new Date(data.endTime).getTime() : null;
        const finRecorridoLoc = data.endLocation || null;

        if (finRecorridoTS && finRecorridoLoc && validUltimoPunto.loc) {
            const diaUltimoSeg = getDayStr(validUltimoPunto.ts);
            const diaCierre = getDayStr(finRecorridoTS);
            const cierreMismoDia = diaUltimoSeg === diaCierre;

            distUltimoACierre = calcularDistancia(
                validUltimoPunto.loc.latitude,
                validUltimoPunto.loc.longitude,
                finRecorridoLoc.latitude,
                finRecorridoLoc.longitude
            );

            if (cierreMismoDia && distUltimoACierre <= 500) {
                usarHasta = finRecorridoTS;
            }
        }

        const tiempoOperativoBruto = Math.round((usarHasta - tsDesde) / (1000 * 60));
        const minutosBreak = data.totalPauseTime || 0;
        const tiempoOperativoEfectivo = Math.max(0, tiempoOperativoBruto - minutosBreak);

        // Construir alertas de penalización
        const alertas = [];
        const penalizacionInicio = (!isDifferentDayLogin && distInicioAFrimero >= 500);
        const cierreLejos = (distUltimoACierre !== null && distUltimoACierre > 500);
        const cierreOtroDia = (finRecorridoTS && validUltimoPunto && getDayStr(finRecorridoTS) !== getDayStr(validUltimoPunto.ts));

        if (isDifferentDayLogin) {
            alertas.push({
                tipo: 'sesion_desfasada',
                mensaje: '📅 Iniciaste recorrido un día diferente. Solo cuenta desde tu primer segmento.'
            });
            aplicaPenalizacion = true;
        }

        if (penalizacionInicio) {
            const minDescuento = Math.round((validPrimerPunto.ts - fechaInicio.getTime()) / (1000 * 60));
            alertas.push({
                tipo: 'inicio_lejos',
                mensaje: `📍 Inicio a ${distInicioAFrimero}m del primer punto (máx 500m). Descuento: ~${minDescuento} min.`
            });
            aplicaPenalizacion = true;
        }

        if (cierreOtroDia) {
            alertas.push({
                tipo: 'cierre_otro_dia',
                mensaje: '📅 Cerraste en día diferente. Solo cuenta hasta tu último segmento del día.'
            });
            aplicaPenalizacion = true;
        } else if (cierreLejos) {
            const minDescuento = Math.round((finRecorridoTS - validUltimoPunto.ts) / (1000 * 60));
            alertas.push({
                tipo: 'cierre_lejos',
                mensaje: `🏁 Cierre a ${distUltimoACierre}m del último punto (máx 500m). Descuento: ~${minDescuento} min.`
            });
            aplicaPenalizacion = true;
        }

        return {
            tiempoTrabajado,
            tiempoOperativoEfectivo,
            distInicioAFrimero,
            aplicaPenalizacion,
            escenario,
            distUltimoACierre,
            alertas,
            tiempoDesfasado: reportData?.tiempoDesfasado || false,
            segmentosDesfasados: reportData?.segmentosDesfasados || 0,
            diferenciaPromedio: reportData?.diferenciaPromedio || 0
        };
    };

    useEffect(() => {
        if (reportData) {
            const result = calcularMetricasAdherencia(reportData);
            setMetrics(result);
        }
    }, [reportData]);

    if (!reportData || !metrics) {
        return (
            <div style={{
                background: '#f0f0f0',
                padding: '20px',
                borderRadius: '8px',
                textAlign: 'center',
                color: '#666'
            }}>
                ℹ️ No hay datos de recorrido para mostrar penalizaciones
            </div>
        );
    }

    return (
        <div style={{ marginBottom: '30px', borderTop: '1px solid rgba(0,0,0,0.1)', paddingTop: '20px' }}>
            <h3 style={{ textAlign: 'center', color: '#333', marginBottom: '15px', fontSize: '1.2rem' }}>
                🕒 Penalizaciones por Adherencia
            </h3>

            {/* CARD DE TIEMPO OPERATIVO */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '15px',
                marginBottom: '20px'
            }}>
                <div style={{
                    background: '#e8f4f8',
                    padding: '15px',
                    borderRadius: '8px',
                    border: '2px solid #17a2b8'
                }}>
                    <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '5px' }}>
                        ⏰ Tiempo Trabajado (Bruto)
                    </div>
                    <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#17a2b8' }}>
                        {formatTiempoOperativo(metrics.tiempoTrabajado)}
                    </div>
                </div>

                <div style={{
                    background: metrics.aplicaPenalizacion ? '#fff3cd' : '#d4edda',
                    padding: '15px',
                    borderRadius: '8px',
                    border: `2px solid ${metrics.aplicaPenalizacion ? '#ffc107' : '#28a745'}`
                }}>
                    <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '5px' }}>
                        🏃 Tiempo Operativo Efectivo
                    </div>
                    <div style={{
                        fontSize: '1.8rem',
                        fontWeight: 'bold',
                        color: metrics.aplicaPenalizacion ? '#856404' : '#28a745'
                    }}>
                        {formatTiempoOperativo(metrics.tiempoOperativoEfectivo)}
                    </div>
                    <div style={{
                        fontSize: '0.85rem',
                        color: metrics.aplicaPenalizacion ? '#856404' : '#28a745',
                        fontWeight: 'bold',
                        marginTop: '5px'
                    }}>
                        {metrics.aplicaPenalizacion ? '⚠️ PENALIZACIÓN APLICADA' : '✅ SIN PENALIZACIONES'}
                    </div>
                </div>
            </div>

            {/* ALERTAS DE PENALIZACIÓN */}
            {metrics.alertas && metrics.alertas.length > 0 && (
                <div style={{
                    background: '#fff3cd',
                    border: '2px solid #ffc107',
                    borderRadius: '8px',
                    padding: '15px',
                    marginBottom: '20px'
                }}>
                    <div style={{
                        background: '#ffc107',
                        color: '#856404',
                        padding: '10px 15px',
                        borderRadius: '6px',
                        marginBottom: '12px',
                        fontWeight: 'bold',
                        fontSize: '1rem'
                    }}>
                        ⚠️ MOTIVOS DE PENALIZACIÓN
                    </div>
                    <div>
                        {metrics.alertas.map((alerta, idx) => (
                            <div
                                key={idx}
                                style={{
                                    background: '#fffbf0',
                                    borderLeft: '4px solid #ffc107',
                                    padding: '10px 12px',
                                    marginBottom: idx < metrics.alertas.length - 1 ? '8px' : '0',
                                    borderRadius: '4px',
                                    color: '#856404',
                                    fontSize: '0.95rem'
                                }}
                            >
                                {alerta.mensaje}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* DETALLES TÉCNICOS */}
            <div style={{
                background: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px',
                padding: '15px',
                fontSize: '0.9rem',
                color: '#666'
            }}>
                <div style={{ marginBottom: '10px' }}>
                    <strong>Escenario:</strong> {metrics.escenario}
                </div>
                <div style={{ marginBottom: '10px' }}>
                    <strong>Distancia Inicio → Primer Punto:</strong> {metrics.distInicioAFrimero}m
                </div>
                {metrics.distUltimoACierre !== null && (
                    <div>
                        <strong>Distancia Último Punto → Cierre:</strong> {metrics.distUltimoACierre}m
                    </div>
                )}
                {metrics.tiempoDesfasado && (
                    <div style={{ marginTop: '10px', color: '#dc3545' }}>
                        <strong>⏳ Desfase Temporal Detectado:</strong> {metrics.segmentosDesfasados} segmentos | Promedio: {metrics.diferenciaPromedio}min
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminPenalizacionesPanel;
